import OpenAI from 'openai';

const DEEPSEEK_API_KEY = import.meta.env.VITE_DEEPSEEK_API_KEY;

const openai = new OpenAI({
  baseURL: 'https://api.deepseek.com/v1',
  apiKey: DEEPSEEK_API_KEY,
  dangerouslyAllowBrowser: true // Required for browser environments
});

interface VerseInfo {
  book: string;
  chapter: number;
  verse?: number;
  text: string;
  version: string;
}

export async function generateVerseInsights(
  reference: string,
  text: string,
  detailed: boolean = false
): Promise<string> {
  const isChapterInsight = !reference.includes(':');
  
  const getPrompt = (detailed: boolean) => {
    if (isChapterInsight) {
      return detailed
        ? 'Provide a comprehensive analysis of this chapter. Include main themes, significant events, theological insights, and practical applications. Organize the response into clear paragraphs.'
        : 'Provide a concise summary of this chapter in a single paragraph. Focus on the main events, key themes, and significant messages.';
    }
    
    return detailed
      ? 'Analyze this verse in exactly 4-5 complete sentences. Include: (1) historical context, (2) theological meaning, (3) practical application, and if relevant (4) cross-references. Keep each sentence focused and concise.'
      : 'Explain the key meaning and significance of this verse in exactly 2 clear, concise sentences.';
  };

  const prompt = `
As a biblical scholar, analyze this ${isChapterInsight ? 'Bible chapter' : 'Bible verse'}:

${isChapterInsight ? 'Chapter' : 'Verse'}: ${text}
Reference: ${reference}

${getPrompt(detailed)}

Important: Ensure each sentence is complete. Do not exceed the requested number of sentences.
Format your response as clear, complete sentences without bullet points or numbering.
`;

  try {
    const completion = await openai.chat.completions.create({
      model: "deepseek-chat",
      messages: [
        { 
          role: "system", 
          content: "You are a knowledgeable biblical scholar providing clear, concise analysis of Bible verses and chapters." 
        },
        { 
          role: "user", 
          content: prompt 
        }
      ],
      max_tokens: isChapterInsight 
        ? (detailed ? 512 : 384)
        : (detailed ? 384 : 128)
    });

    let generatedText = completion.choices[0].message.content?.trim() || '';
    
    // Clean up the response
    generatedText = generatedText
      .replace(/^(Response:|Assistant:)/i, '') 
      .replace(/\n+/g, ' ') 
      .trim();
      
    // Ensure the response ends with proper punctuation
    if (!generatedText.endsWith('.') && !generatedText.endsWith('!') && !generatedText.endsWith('?')) {
      const lastCompleteIndex = Math.max(
        generatedText.lastIndexOf('.'),
        generatedText.lastIndexOf('!'),
        generatedText.lastIndexOf('?')
      );
      if (lastCompleteIndex > 0) {
        generatedText = generatedText.substring(0, lastCompleteIndex + 1);
      }
    }
    
    return generatedText;
  } catch (error) {
    throw new Error('Failed to generate insights. Please try again.');
  }
}

export async function answerQuestion(question: string, context: string): Promise<{ answer: string; confidence?: number; error?: string }> {
  try {
    const completion = await openai.chat.completions.create({
      model: "deepseek-reasoner",
      messages: [
        { 
          role: "system", 
          content: "You are a knowledgeable biblical scholar. Provide clear, concise answers based on the biblical context provided." 
        },
        { 
          role: "user", 
          content: `Context: ${context}\n\nQuestion: ${question}` 
        }
      ],
      max_tokens: 500
    });

    return {
      answer: completion.choices[0].message.content || "I apologize, but I couldn't generate a response.",
      confidence: 0.9
    };
  } catch (error) {
    console.error('Error answering question:', error);
    return {
      answer: "I apologize, but I'm having trouble processing your question right now. Please try again later.",
      error: error instanceof Error ? error.message : 'Unknown error',
      confidence: 0
    };
  }
}
